import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function EmptyState({ loading, text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 13 }}>
      {loading ? "Cargando..." : text}
    </div>
  )
}

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function SummaryMetric({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function HeatCell({ hour, count, intensity, getHeatColor }) {
  const bg = getHeatColor(intensity)
  const isLight = String(intensity || "").toUpperCase() === "EMPTY"

  return (
    <div
      style={{
        borderRadius: 14,
        padding: 10,
        background: bg,
        color: isLight ? "#0f172a" : "#ffffff",
        minHeight: 76,
        display: "grid",
        alignContent: "space-between",
        gap: 6,
        border: isLight ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,0.14)"
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          opacity: isLight ? 0.8 : 0.95
        }}
      >
        {hour}
      </div>

      <div style={{ display: "grid", gap: 2 }}>
        <div
          style={{
            fontSize: 22,
            lineHeight: 1,
            fontWeight: 900
          }}
        >
          {formatCount(count)}
        </div>

        <div
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            opacity: isLight ? 0.75 : 0.9
          }}
        >
          carga estimada
        </div>
      </div>
    </div>
  )
}

export default function HeatmapPanel({
  heatmap = [],
  collapsed,
  onToggle,
  loading,
  isMobile,
  getHeatColor
}) {
  const rows = Array.isArray(heatmap) ? heatmap : []

  const totalLoad = rows.reduce(
    (acc, item) => acc + toNumber(item?.count),
    0
  )

  const activeHours = rows.filter((item) => toNumber(item?.count) > 0).length

  const topHour = [...rows].sort(
    (a, b) => toNumber(b?.count) - toNumber(a?.count)
  )[0]

  return (
    <SectionCard
      title="Mapa de ocupación del día"
      subtitle="Dónde se concentra la operación por hora"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <>
          {!rows.length ? (
            <EmptyState loading={loading} text="Sin mapa de ocupación disponible." />
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                  gap: 10
                }}
              >
                <SummaryMetric
                  label="Carga total"
                  value={formatCount(totalLoad)}
                  helper="Suma de actividad detectada en el día"
                />

                <SummaryMetric
                  label="Horas activas"
                  value={formatCount(activeHours)}
                  helper="Bloques con movimiento u ocupación"
                />

                <SummaryMetric
                  label="Pico horario"
                  value={topHour?.hour || "-"}
                  helper={
                    topHour
                      ? `${formatCount(topHour?.count)} de carga en ese bloque`
                      : "Sin hora pico detectada"
                  }
                />
              </div>

              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background:
                    "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
                  border: "1px solid #dbeafe",
                  color: "#1e293b",
                  fontSize: 13,
                  lineHeight: 1.45,
                  fontWeight: 700
                }}
              >
                {topHour
                  ? `Mayor concentración en ${topHour.hour}. Conviene revisar si esa franja está bien balanceada o si está absorbiendo demasiada demanda del día.`
                  : "Sin suficiente actividad para detectar un patrón horario dominante."}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(3, minmax(0, 1fr))"
                    : "repeat(auto-fit, minmax(96px, 1fr))",
                  gap: 8,
                  alignItems: "start"
                }}
              >
                {rows.map((item) => (
                  <HeatCell
                    key={item.hour}
                    hour={item.hour}
                    count={item.count}
                    intensity={item.intensity}
                    getHeatColor={getHeatColor}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatCount(value) {
  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(toNumber(value))
}